import React, { useCallback, useEffect, useRef, createContext } from 'react'
import WindowContainer from 'GeneralUI/WindowContainer/WindowContainer.jsx'
import { useWorkflow, useUIConfig, useScreen } from 'hooks/useURL'
import { reconstructRoute, push } from 'Helper/routeHelpers'
import { setScreen } from 'actions/interactableActions'
import { useSelector, useDispatch } from 'react-redux'
import WorkflowDisplay from './WorkflowDisplay.jsx'

export const WorkflowContext = createContext()

const WorkflowWindowContainer = (props) => {
  const params = useSelector(state => state.router.match.params)
  const pathname = useSelector(state => state.router.location.pathname)
  const isDesignMode = useSelector(state => state.interactableState.uiDesignMode) != null
  const { screens: availableScreens = [] } = useUIConfig()
  const { workflow, step = 1 } = useWorkflow()
  const interactableWindows = useSelector(state => state.interactableState.windows)
  const interactableWindowsRef = useRef(interactableWindows)
  useEffect(() => {
    interactableWindowsRef.current = interactableWindows
  }, [interactableWindows])
  const dispatch = useDispatch()
  const screen = useScreen(false)

  // screen handling for when there is a screen in the path but no workflow
  useEffect(() => {
    if (screen?.path && (!workflow || !step)) {
      dispatch(setScreen(screen))
    }
    // made this dependent on screen.path because if the path doesn't change its the same
    // screen even though x/y coords of windows within the screen may be changing.
    // in this case, it is also dependant on patientId because if the path remains the same
    // but a new patient is selected, the screen should be reloaded.
  }, [screen?.path, params?.patientId])

  useEffect(() => {
    let nextScreen
    if (
      workflow && workflow.sequence && step && workflow.sequence[step - 1]
    ) {
      let { screenPath, url } = workflow.sequence[step - 1]
      if (screenPath != null) {
        nextScreen = availableScreens.find(v => v.path === screenPath) || {
          windows: Object.values(interactableWindowsRef.current)
        }
        dispatch(setScreen(nextScreen))
      } else if (url && isDesignMode) {
        dispatch(setScreen([]))
      } else if (url) {
        if (url.match(':patientId')) {
          url = url.replace(':patientId', params?.patientId)
        }
        push(url)
      }
    }
  }, [workflow, step, params?.patientId, availableScreens, isDesignMode])

  const stepChangeHandler = useCallback((requestedStep = null) => {
    if (requestedStep == null) {
      // if no step was requested, move to next step (0 based so url step includes +1)
      requestedStep = parseInt(step)
    }
    const maxStep = workflow.sequence.length
    requestedStep++ // add one since it was 0 based.
    if (
      requestedStep > 0 && requestedStep <= maxStep
    ) {
      push(
        reconstructRoute(params, {
          workflow: workflow.path,
          step: requestedStep,
          screen: null
        }, pathname)
      )
    } else if (requestedStep > maxStep) {
      push(
        reconstructRoute(params, {
          screen: null,
          workflow: null
        }, pathname)
      )
    }
  }, [step, workflow, params, pathname])

  const abortWorkflow = useCallback(() => {
    // to abort the workflow we need to push the current workflow
    // steps screen and a screen path.
    const stepIndex = step - 1
    const equivalentScreen = workflow?.sequence[stepIndex]?.screenPath || null
    push(
      reconstructRoute(params, {
        screen: equivalentScreen,
        workflow: null
      }, pathname)
    )
  }, [step, workflow, params, pathname])

  return (
    <WorkflowContext.Provider value={stepChangeHandler}>
      {
        workflow == null
          ? null
          : <WorkflowDisplay
              workflow={workflow}
              workflowStep={step - 1}
              stepChangeHandler={stepChangeHandler}
              cancelWorkflow={abortWorkflow}
            />
      }
      <WindowContainer inWorkflow = {workflow != null} verticalOffset={props.verticalOffset} horizontalOffset={props.horizontalOffset}>
        {props.children}
      </WindowContainer>
    </WorkflowContext.Provider>
  )
}

export default WorkflowWindowContainer
